/**
 * Adding a student to the sheet.
 *
 * Mirrors NewAssessmentModal, reached the same way: a "+" at the end of the
 * roster rather than a typed-into row. The row version only ever collected a
 * name, which left the OEN with no entry point at all — a teacher had to add
 * the student, find their row, then type into the OEN cell. Collecting both at
 * once is one step instead of three, and it makes the OEN visible as something
 * a student HAS rather than a column that happens to exist.
 *
 * The OEN stays optional, as it is everywhere else: plenty of students do not
 * have one, and blocking on it would make those students unenterable.
 */

import { useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/** Same field treatment as the assessment modal, so the two agree on sight. */
const FIELD = [
  "h-9 w-full border border-border bg-background px-2 text-[13px] text-foreground",
  "outline-none transition-colors focus:border-primary",
].join(" ");

const LABEL = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";

export default function NewStudentModal({ onCreate, onClose }) {
  const [name, setName] = useState("");
  const [oen, setOen] = useState("");

  const submit = (e) => {
    e.preventDefault();
    // A nameless row is not a student; the backend refuses it too.
    if (!name.trim()) return;
    onCreate({ name: name.trim(), oen: oen.trim() || null });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-[440px] rounded-xl border border-border bg-card shadow-card"
      >
        <div className="flex items-start justify-between border-b border-border px-5 py-4">
          <div>
            <h2 className="font-display text-xl leading-tight text-foreground">New student</h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              They appear at the end of the roster, ready to mark.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Close without saving"
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div>
            <label className={LABEL} htmlFor="ns-name">Name</label>
            <input
              id="ns-name"
              value={name}
              autoFocus
              placeholder="Student name"
              onChange={(e) => setName(e.target.value)}
              className={cn(FIELD, "font-medium")}
            />
          </div>

          <div>
            <label className={LABEL} htmlFor="ns-oen">
              OEN <span className="normal-case tracking-normal text-muted-foreground/70">- optional</span>
            </label>
            <input
              id="ns-oen"
              value={oen}
              inputMode="numeric"
              placeholder="Ontario Education Number"
              onChange={(e) => setOen(e.target.value)}
              className={cn(FIELD, "tabular-nums")}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-4">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" disabled={!name.trim()}>Add student</Button>
        </div>
      </form>
    </div>
  );
}
